/**
 * Annullamento della registrazione dell'ultimo step CRA Art.14(2) di un report ENISA.
 *
 * Registrare uno step (CTA «Registra» del banner → conferma) ferma il suo timer SLA. Se lo step
 * non è stato davvero inviato sul sito ENISA (registrazione per errore) l'analista lo annulla
 * qui, con una motivazione obbligatoria: lo step torna da fare e il timer riparte dalla scadenza
 * originale (POST /srp/reports/:id/undo-step, tracciato nel log audit, notifica interna).
 *
 * Si annulla solo l'ULTIMO step registrato in ordine (relazione finale → 72h → early warning),
 * così la sequenza resta coerente. Visibile solo con VULNERABILITY_ANALYSIS.
 */

import { ModalPortal } from '../../components/ModalPortal.js';
import { useState } from 'react';
import { ApiError } from '../../api/client.js';
import { usePermissions } from '../../auth/usePermissions.js';
import { useUndoStep } from './hooks.js';
import { SLA_STEP_ORDER } from './slaDeadlines.js';
import type { NotificationStep, SrpReport } from './types.js';
import styles from './Srp.module.css';

const STEP_NAME: Record<NotificationStep, string> = {
  early_warning: 'Early warning',
  vulnerability: 'Notifica 72h',
  final: 'Relazione finale',
};

function fmtData(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

export function SrpStepUndo({ report }: { report: SrpReport }) {
  const { can } = usePermissions();
  const undo = useUndoStep(report.id);
  const [aperto, setAperto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [errore, setErrore] = useState<string | null>(null);

  const notifiche = report.payload.notifications ?? [];
  const ultimoStep = [...SLA_STEP_ORDER].reverse().find((s) => notifiche.some((n) => n.step === s));
  const ultima = notifiche.find((n) => n.step === ultimoStep);
  if (!can('VULNERABILITY_ANALYSIS') || !ultimoStep || !ultima) return null;

  async function conferma() {
    if (!ultimoStep) return;
    setErrore(null);
    try {
      await undo.mutateAsync({ step: ultimoStep, reason: motivo.trim() });
      setAperto(false);
      setMotivo('');
    } catch (err) {
      setErrore(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Errore durante l'annullamento",
      );
    }
  }

  return (
    <div className={styles.muted ?? ''} data-testid="srp-undo-step">
      Ultimo step registrato: «{STEP_NAME[ultimoStep]}» il {fmtData(ultima.sentAt)} — timer fermato.{' '}
      <button
        type="button"
        className={styles.btnSecondary}
        data-testid="undo-step-apri"
        title="Lo step non è stato davvero inviato a ENISA? Annulla la registrazione: lo step torna da fare e il suo timer riparte."
        onClick={() => {
          setErrore(null);
          setMotivo('');
          setAperto(true);
        }}
      >
        Annulla registrazione
      </button>
      {aperto && (
        <ModalPortal onClose={() => setAperto(false)}>
          <div className={styles.dialogOverlay} data-testid="dialog-undo-step" role="dialog">
            <div className={styles.dialogBox}>
              <p className={styles.dialogTitle}>Annulla registrazione «{STEP_NAME[ultimoStep]}»</p>
              <p className={styles.dialogBody}>
                Usalo solo se lo step NON è stato inviato sul sito ENISA. Lo step torna da fare e il
                suo timer SLA riparte dalla scadenza originale (può risultare già scaduto). La
                registrazione annullata e la motivazione restano nel log audit.
              </p>
              <textarea
                className={styles.stepNoteInput}
                data-testid="undo-step-motivo"
                rows={3}
                placeholder="Motivazione (obbligatoria)"
                value={motivo}
                onChange={(e) => {
                  setMotivo(e.target.value);
                }}
                autoFocus
              />
              {errore && (
                <span className={styles.inlineMutError} data-testid="errore-undo-step">
                  {errore}
                </span>
              )}
              <div className={styles.dialogActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  data-testid="undo-step-chiudi"
                  disabled={undo.isPending}
                  onClick={() => {
                    setAperto(false);
                  }}
                >
                  Chiudi
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  data-testid="undo-step-conferma"
                  disabled={undo.isPending || motivo.trim().length === 0}
                  onClick={() => void conferma()}
                >
                  {undo.isPending ? 'Annullamento…' : 'Annulla registrazione'}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}
